/**
 * MeasurementCache - Caches expensive DOM measurements and handles invalidation
 *
 * This class stores all dimension and position measurements needed for sticky calculations.
 * It prevents redundant getBoundingClientRect() calls on every scroll event.
 */

export class MeasurementCache {
	constructor() {
		this.cache = {};
		this.valid = false;
		this.stableValid = false; // Stable values (offset, height) remain valid on layout changes
		this.timestamp = null;
	}

	/**
	 * Measure all required dimensions and positions
	 * @param {Object} elements - Object containing all required DOM elements
	 * @returns {Object} The measured values
	 */
	measure(elements) {
		if (!elements || !elements.stickyElement) {
			this.valid = false;
			return null;
		}

		try {
			// Sticky header height from CSS variable (works on all breakpoints)
			const stickyHeaderHeight = parseInt(
				getComputedStyle(document.documentElement)
					.getPropertyValue('--header-sticky-height')
			) || 60;
			this.cache.navHeight = stickyHeaderHeight;

			// Main content top position (absolute positioning parent)
			const mainContentRect = elements.mainContent?.getBoundingClientRect();
			this.cache.mainContentTop = mainContentRect
				? window.scrollY + mainContentRect.top
				: 0;

			// Product section (container) top position
			const containerRect = elements.container.getBoundingClientRect();
			this.cache.containerTop = window.scrollY + containerRect.top;

			// Sticky element height and RELATIVE offset from container
			// We store the offset instead of absolute position because:
			// - The offset doesn't change when other sections load above
			// - Absolute position can be computed: containerTop + offset
			const stickyRect = elements.stickyElement.getBoundingClientRect();
			this.cache.stickyHeight = stickyRect.height;
			this.cache.stickyOffsetFromContainer = stickyRect.top - containerRect.top;

			// Stop element top position
			if (elements.stopElement) {
				const stopRect = elements.stopElement.getBoundingClientRect();
				this.cache.stopElementTop = window.scrollY + stopRect.top;
			} else {
				this.cache.stopElementTop = null;
			}

			this.valid = true;
			this.stableValid = true;
			this.timestamp = Date.now();

			return this.cache;
		} catch (error) {
			console.error('Error measuring sticky product elements:', error);
			this.valid = false;
			this.stableValid = false;
			return null;
		}
	}

	/**
	 * Remeasure only layout-dependent values (container/stop positions)
	 * Use when sections load and push content down, but sticky element itself hasn't changed
	 * @param {Object} elements - Object containing all required DOM elements
	 */
	measureLayout(elements) {
		if (!elements?.container) {
			return;
		}

		// Remeasure container position
		const containerRect = elements.container.getBoundingClientRect();
		this.cache.containerTop = window.scrollY + containerRect.top;

		// Remeasure main content position
		const mainContentRect = elements.mainContent?.getBoundingClientRect();
		this.cache.mainContentTop = mainContentRect
			? window.scrollY + mainContentRect.top
			: 0;

		// Remeasure stop element position
		if (elements.stopElement) {
			const stopRect = elements.stopElement.getBoundingClientRect();
			this.cache.stopElementTop = window.scrollY + stopRect.top;
		}

		this.valid = true;
		this.timestamp = Date.now();
	}

	/**
	 * Mark the cache as fully invalid (needs full remeasurement)
	 */
	invalidate() {
		this.valid = false;
		this.stableValid = false;
	}

	/**
	 * Mark only layout-dependent values as invalid
	 * Stable values (stickyHeight, stickyOffsetFromContainer, navHeight) remain valid
	 */
	invalidateLayout() {
		this.valid = false;
		// stableValid stays true
	}

	/**
	 * Check if cache is valid
	 * @returns {boolean}
	 */
	isValid() {
		return this.valid;
	}

	/**
	 * Check if stable values are still valid (can use measureLayout instead of full measure)
	 * @returns {boolean}
	 */
	hasStableValues() {
		return this.stableValid;
	}

	/**
	 * Get a specific cached value
	 * @param {string} key - The measurement key
	 * @returns {*} The cached value or null if invalid
	 */
	get(key) {
		return this.valid ? this.cache[key] : null;
	}

	/**
	 * Get all cached measurements
	 * @returns {Object|null} All measurements or null if invalid
	 */
	getAll() {
		return this.valid ? { ...this.cache } : null;
	}
}
